<script lang="ts">
  import { createForm } from 'svelte-forms-lib'
  import { DoubleBounce } from 'svelte-loading-spinners'
  import { fieldErrorsOrUndefined } from '../utilities/forms'
  import * as T from 'io-ts'
  import { withMessage, NonEmptyString } from 'io-ts-types'
  import FormGroup from './EmailSignInFormGroup.svelte'
  import CtaButton from '../Generic/CtaButton.svelte'
  import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth'
  import { auth } from '../Firebase'
  import type { EmailSignInFormProps } from './EmailSignInFormProps'
  import { fromEvent, Observable, withLatestFrom } from 'rxjs'
  import { map, filter, exhaustMap } from 'rxjs/operators'
  import { SupportEmail } from '../utilities/Constants'
  import { generateCurrentAppLocation } from '../utilities/helpers'
  import { onMount } from 'svelte'

  let initialValues: EmailSignInFormProps = {
    email: '' as NonEmptyString,
    password: '' as NonEmptyString,
  }
  let message = ''
  let loading = false

  const EmailValidationError = 'Please enter your email address.'
  const EmailT = withMessage(NonEmptyString, () => EmailValidationError)
  const FormT = T.type({
    email: EmailT,
    password: withMessage(NonEmptyString, () => 'Please enter a password.'),
  })

  const userFriendlyFirebaseError = (error: unknown): string => {
    if (!(error instanceof Error)) {
      return `I'm afraid something went wrong. Please contact ${SupportEmail} and we will try to help.`
    }

    if (error.message.match(/user-not-found/)) {
      return 'No User was found with that email.'
    } else if (error.message.match(/wrong-password/)) {
      return 'Incorrect password'
    }

    return `Error: ${error.message}`
  }

  const { form, errors, touched, isValid, isSubmitting, handleChange, handleSubmit } = createForm({
    initialValues,
    validate: values => fieldErrorsOrUndefined(T.partial(FormT.props), values),
    onSubmit: async (values: T.TypeOf<typeof FormT>) => {
      loading = true
      try {
        await signInWithEmailAndPassword(auth, values.email, values.password)
      } catch (error: unknown) {
        message = userFriendlyFirebaseError(error)
      }
      loading = false
    },
  })

  const form_observable: Observable<EmailSignInFormProps> = new Observable(observer => {
    const unsub = form.subscribe(values => observer.next(values))
    return unsub
  })

  const touched_observable: Observable<Record<keyof EmailSignInFormProps, boolean>> = new Observable(
    observer => {
      const unsub = touched.subscribe(values => observer.next(values))
      return unsub
    },
  )

  let forgottenPasswordButton: HTMLButtonElement | undefined
  const forgottenPasswordValid = form_observable.pipe(
    withLatestFrom(touched_observable),
    filter(([_, touched]) => touched.email),
    map(([{ email }, _]) => {
      const result = EmailT.is(email)
      if (!result) {
        errors.update(current => ({ ...current, email: EmailValidationError }))
      }
      return result
    }),
  )

  const requestPasswordResetEmail = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email, {
        url: generateCurrentAppLocation(document),
      })
      message = 'Check your emails for a password reset link.'
    } catch (error: unknown) {
      message = userFriendlyFirebaseError(error)
    }
  }

  onMount(() => {
    if (forgottenPasswordButton) {
      const sub = fromEvent(forgottenPasswordButton, 'click')
        .pipe(
          withLatestFrom(form_observable),
          exhaustMap(([_, { email }]) => requestPasswordResetEmail(email)),
        )
        .subscribe()
      return () => sub.unsubscribe()
    }
  })

  touched.subscribe(() => (message = ''))
</script>

{#if loading}
  <div class="spinner">
    <DoubleBounce color="#4b4b4b" size="2" unit="em" />
  </div>
{/if}

<div id="container">
  <form on:submit={handleSubmit}>
    <div class="grid">
      <FormGroup key="email" label="Email" {form} {errors} {handleChange} />
      <FormGroup key="password" label="Password" password {form} {errors} {handleChange} />
      <div id="submit-buttons">
        <CtaButton bind:inner={forgottenPasswordButton} disabled={!$forgottenPasswordValid}>
          Forgotten Password
        </CtaButton>
        <CtaButton type="submit" disabled={!$isValid || $isSubmitting}>Sign In</CtaButton>
      </div>
      {#if message}
        <h1>{message}</h1>
      {/if}
    </div>
  </form>
</div>

<style>
  .spinner {
    display: flex;
    position: absolute;
    justify-content: center;
    text-align: center;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    font-size: 60px;
  }
  .grid {
    padding: 2em;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    align-items: center;
  }

  #submit-buttons {
    grid-column: 1 / span 3;
    display: flex;
    justify-content: space-evenly;
    margin: 2em 0em;
  }

  h1 {
    grid-column: 1 / span 3;
  }

  #container {
    width: 100%;
    max-width: 75em;
  }
</style>
